import React, { useState } from 'react';
import { updatePassword, EmailAuthProvider, reauthenticateWithCredential } from 'firebase/auth';
import { auth } from '../firebase';
import { Lock, Eye, EyeOff, Shield, AlertCircle } from 'lucide-react';

function ChangePasswordPrompt({ onComplete }) {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (newPassword.length < 8) {
      setError('New password must be at least 8 characters.');
      return;
    }

    if (newPassword === currentPassword) {
      setError('New password must be different from your temporary password.');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setLoading(true);
    try {
      const user = auth.currentUser;
      // Re-authenticate with current (temporary) password
      const credential = EmailAuthProvider.credential(user.email, currentPassword);
      await reauthenticateWithCredential(user, credential);

      // Update to new password
      await updatePassword(user, newPassword);

      onComplete();
    } catch (err) {
      if (err.code === 'auth/wrong-password' || err.code === 'auth/invalid-credential') {
        setError('Current password is incorrect. Enter the temporary password you were given.');
      } else if (err.code === 'auth/weak-password') {
        setError('Password is too weak. Please use at least 8 characters with a mix of letters and numbers.');
      } else {
        setError('Failed to change password. Please try again.');
        console.error('Password change error:', err);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 9999,
      background: 'linear-gradient(180deg, #e8f0ed 0%, #f8f6f3 100%)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: '16px'
    }}>
      <div style={{
        background: 'white', borderRadius: '24px', maxWidth: '440px', width: '100%',
        boxShadow: '0 8px 24px rgba(0,0,0,0.12)', overflow: 'hidden'
      }}>
        {/* Header */}
        <div style={{
          background: 'linear-gradient(135deg, #2c5f4e 0%, #3d7a66 100%)',
          padding: '28px 32px', textAlign: 'center'
        }}>
          <div style={{
            width: '56px', height: '56px', background: 'rgba(255,255,255,0.15)',
            borderRadius: '12px', display: 'inline-flex', alignItems: 'center',
            justifyContent: 'center', marginBottom: '12px',
            border: '1px solid rgba(255,255,255,0.2)'
          }}>
            <Shield size={28} color="white" />
          </div>
          <h2 style={{
            margin: 0, color: 'white', fontFamily: "Georgia, serif",
            fontSize: '22px', fontWeight: 600
          }}>
            Set Your Password
          </h2>
          <p style={{ margin: '6px 0 0', color: '#b8926a', fontSize: '14px', fontStyle: 'italic' }}>
            Secure your account
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} style={{ padding: '28px 32px 32px' }}>
          <p style={{ margin: '0 0 20px', color: '#6b7280', fontSize: '14px', lineHeight: 1.6 }}>
            Welcome! For security, please change your temporary password to something personal. You'll use this to log in from now on.
          </p>

          {error && (
            <div style={{
              display: 'flex', alignItems: 'flex-start', gap: '8px',
              padding: '12px 16px', background: '#fef2f2', border: '1px solid #fecaca',
              borderRadius: '10px', marginBottom: '16px', color: '#991b1b', fontSize: '13px'
            }}>
              <AlertCircle size={16} style={{ flexShrink: 0, marginTop: '1px' }} />
              {error}
            </div>
          )}

          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#374151', marginBottom: '6px' }}>
              <Lock size={14} style={{ verticalAlign: '-2px', marginRight: '4px' }} />
              Temporary Password
            </label>
            <div style={{ position: 'relative' }}>
              <input
                type={showCurrent ? 'text' : 'password'}
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                placeholder="Enter the password you were given"
                required
                style={{
                  width: '100%', padding: '10px 40px 10px 12px', border: '1px solid #d1d5db',
                  borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box'
                }}
              />
              <button type="button" onClick={() => setShowCurrent(!showCurrent)} style={{
                position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)',
                background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280', padding: '2px'
              }}>
                {showCurrent ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>

          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#374151', marginBottom: '6px' }}>
              New Password
            </label>
            <div style={{ position: 'relative' }}>
              <input
                type={showNew ? 'text' : 'password'}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Choose a new password (8+ characters)"
                required
                minLength={8}
                style={{
                  width: '100%', padding: '10px 40px 10px 12px', border: '1px solid #d1d5db',
                  borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box'
                }}
              />
              <button type="button" onClick={() => setShowNew(!showNew)} style={{
                position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)',
                background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280', padding: '2px'
              }}>
                {showNew ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>

          <div style={{ marginBottom: '24px' }}>
            <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#374151', marginBottom: '6px' }}>
              Confirm New Password
            </label>
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Re-enter your new password"
              required
              style={{
                width: '100%', padding: '10px 12px', border: '1px solid #d1d5db',
                borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box'
              }}
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            style={{
              width: '100%', padding: '14px', border: 'none', borderRadius: '12px',
              background: loading ? '#9ca3af' : 'linear-gradient(135deg, #2c5f4e 0%, #3d7a66 100%)',
              color: 'white', fontSize: '16px', fontWeight: 600, cursor: loading ? 'default' : 'pointer',
              boxShadow: '0 4px 12px rgba(44, 95, 78, 0.3)'
            }}
          >
            {loading ? 'Updating...' : 'Set New Password'}
          </button>
        </form>
      </div>
    </div>
  );
}

export default ChangePasswordPrompt;
